import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShield } from '@fortawesome/free-solid-svg-icons';
import { PLATFORM_PERMISSION_MATRIX } from '@/lib/platform-permissions';
import type { PlatformRole } from '@/types/platform-user.types';
import { RoleBadge } from './user-presentation';

/**
 * Phase 12.6-5 — the role reference card, DERIVED (OD-C, design §C).
 *
 * ── What this replaced ──────────────────────────────────────────────────────
 * A hardcoded seven-entry list that claimed Platform Support **"May request
 * impersonation sessions"**. Impersonation has three permission strings in the
 * matrix and **no schema, no service, no route and no code** — verified
 * exhaustively for the design. The card advertised a feature that does not
 * exist, which is the exact defect OD-C was raised to fix.
 *
 * ── What "derived" means here, precisely ────────────────────────────────────
 * **The role list is generated from the keys of `PLATFORM_PERMISSION_MATRIX`**,
 * not hand-written. A role added to or removed from the matrix cannot leave this
 * card stale.
 *
 * **Descriptions are curated per role**, because `platform:companies:suspend` is
 * not end-user language. They are curated, not invented: each one summarises the
 * permissions that role actually holds in the matrix, and nothing else.
 *
 * ── The impersonation exclusion, by name ────────────────────────────────────
 * The three impersonation permissions are excluded through a single named
 * constant carrying the reason — not scattered conditionals, and not silent
 * omission. Design §C requires that the exclusion be visible in the code.
 *
 * ── The anti-drift guarantee ────────────────────────────────────────────────
 * Every permission in the matrix must be either covered by a role's curated copy
 * or listed in `UNIMPLEMENTED_PLATFORM_PERMISSIONS`. `PERMISSION_COVERAGE` below
 * is the machine-checkable form of that claim, and **12.6-6 owns the test that
 * asserts it**. Without it, "derived" would be a word rather than a property.
 */

/**
 * Permissions that exist in the matrix but have **no implementation at all**.
 *
 * `platform:impersonation:request` / `:approve` / `:view-log` are granted to
 * three roles and back nothing: no schema, no service, no route, no code. They
 * are excluded from this card so it never advertises a capability the product
 * does not have.
 *
 * **Removing an entry from this list is the job of the phase that builds the
 * feature.** Impersonation would need its own design (§9 lists it as [D] —
 * deferred, "needs its own design"). Until then it stays here, named, with this
 * comment.
 */
export const UNIMPLEMENTED_PLATFORM_PERMISSIONS: readonly string[] = [
  'platform:impersonation:request',
  'platform:impersonation:approve',
  'platform:impersonation:view-log',
] as const;

/**
 * Curated, human-readable copy per role.
 *
 * `Record<PlatformRole, …>` so a role added to the union without copy here is a
 * compile error rather than a blank card entry.
 *
 * `covers` lists the permissions each description accounts for. It is what makes
 * the anti-drift assertion possible: a permission that appears in the matrix but
 * in no `covers` array and in no exclusion list is a description that has
 * silently fallen behind.
 */
interface RoleCopy {
  summary: string;
  /** Short capability bullets. Never a raw permission string. */
  capabilities: string[];
  /** What this role explicitly cannot do, where the omission would mislead. */
  limitation?: string;
  covers: readonly string[];
}

const ROLE_COPY: Record<PlatformRole, RoleCopy> = {
  'platform-owner': {
    summary: 'Full control of the platform, including who else works on it.',
    capabilities: [
      'Everything every other role can do',
      'The only role that can create, edit, re-role, deactivate or sign out platform users',
    ],
    // The owner holds the wildcard, so it covers the matrix by definition.
    covers: ['*'],
  },
  'platform-admin': {
    summary: 'Day-to-day administration of companies, subscriptions and content.',
    capabilities: [
      'Create, update and suspend companies',
      'Manage subscriptions; view billing',
      'Manage master templates and procedures',
      'View system health, background jobs and the audit log',
      'Manage support',
      'View the platform user list',
    ],
    // ── The correction §C REQUIRES ──────────────────────────────────────────
    // The v0.1 placeholder omitted this, which on a page about managing platform
    // users implied the opposite. `platform:platform-users:manage` is owner-only.
    limitation:
      'Cannot manage platform users — viewing this page is read-only for this role. Creating, editing, re-roling, deactivating and revoking sessions all require Platform Owner.',
    covers: [
      'platform:companies:view',
      'platform:companies:create',
      'platform:companies:update',
      'platform:companies:suspend',
      'platform:subscriptions:view',
      'platform:subscriptions:manage',
      'platform:billing:view',
      'platform:templates:view',
      'platform:templates:manage',
      'platform:procedures:view',
      'platform:procedures:manage',
      'platform:platform-users:view',
      'platform:audit-log:view',
      'platform:system:view',
      'platform:system:manage',
      'platform:jobs:view',
      'platform:jobs:manage',
      'platform:support:view',
      'platform:support:manage',
    ],
  },
  'platform-operations': {
    summary: 'Runs support and watches the platform; reads companies and content.',
    capabilities: [
      'View companies, subscriptions, templates and procedures',
      'View system health and background jobs',
      'Manage support',
    ],
    limitation: 'Cannot view or manage platform users.',
    covers: [
      'platform:companies:view',
      'platform:subscriptions:view',
      'platform:templates:view',
      'platform:procedures:view',
      'platform:system:view',
      'platform:jobs:view',
      'platform:support:view',
      'platform:support:manage',
    ],
  },
  'platform-finance': {
    summary: 'Owns the commercial side — billing and subscriptions.',
    capabilities: ['Manage billing', 'Manage subscriptions', 'View companies'],
    limitation: 'Cannot view or manage platform users.',
    covers: [
      'platform:companies:view',
      'platform:subscriptions:view',
      'platform:subscriptions:manage',
      'platform:billing:view',
      'platform:billing:manage',
    ],
  },
  'platform-support': {
    summary: 'Front-line support; reads company data to answer questions.',
    capabilities: ['View companies, templates and procedures', 'Manage support'],
    limitation: 'Cannot view or manage platform users.',
    covers: [
      'platform:companies:view',
      'platform:templates:view',
      'platform:procedures:view',
      'platform:support:view',
      'platform:support:manage',
    ],
  },
  'platform-developer': {
    summary: 'Technical operations — system health and background processing.',
    capabilities: ['Manage system health', 'Manage background jobs'],
    limitation: 'Cannot view companies, subscriptions or platform users.',
    covers: [
      'platform:system:view',
      'platform:system:manage',
      'platform:jobs:view',
      'platform:jobs:manage',
    ],
  },
  'platform-auditor': {
    summary: 'Read-only oversight across the platform.',
    capabilities: [
      'View companies, subscriptions and billing',
      'View templates and procedures',
      'View the platform user list and the audit log',
      'View system health, jobs and support',
    ],
    limitation: 'Read-only everywhere — changes nothing.',
    covers: [
      'platform:companies:view',
      'platform:subscriptions:view',
      'platform:billing:view',
      'platform:templates:view',
      'platform:procedures:view',
      'platform:platform-users:view',
      'platform:audit-log:view',
      'platform:system:view',
      'platform:jobs:view',
      'platform:support:view',
    ],
  },
};

/**
 * Every permission this card's curated copy accounts for.
 *
 * Exported so 12.6-6's anti-drift test can assert the invariant §C requires:
 * every permission in `PLATFORM_PERMISSION_MATRIX` is either in here or in
 * `UNIMPLEMENTED_PLATFORM_PERMISSIONS`.
 */
export const PERMISSION_COVERAGE: ReadonlySet<string> = new Set(
  Object.values(ROLE_COPY).flatMap((copy) => copy.covers),
);

/** The role list, DERIVED from the matrix keys — never hand-written. */
const DERIVED_ROLES = Object.keys(PLATFORM_PERMISSION_MATRIX) as PlatformRole[];

export function PlatformRoleReferenceCard() {
  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <div className="mb-1.5 flex items-center gap-2">
        <FontAwesomeIcon icon={faShield} className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold text-foreground">Platform roles reference</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        What each role can do. Authority is enforced by the server on every request — this is a
        summary, not the rule itself.
      </p>

      <ul className="grid list-none grid-cols-1 gap-3 lg:grid-cols-2">
        {DERIVED_ROLES.map((role) => {
          const copy = ROLE_COPY[role];
          if (!copy) return null;
          return (
            <li key={role} className="rounded-lg border border-border p-3.5">
              <RoleBadge role={role} />
              <p className="mt-2 text-sm text-foreground">{copy.summary}</p>
              <ul className="mt-2 list-disc space-y-1 pl-4">
                {copy.capabilities.map((capability) => (
                  <li key={capability} className="text-xs text-muted-foreground">
                    {capability}
                  </li>
                ))}
              </ul>
              {copy.limitation && (
                <p className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Limitation: </span>
                  {copy.limitation}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
